/**
 * Synchronous one-shot lock used by Retry buttons. A `useRef`-backed boolean
 * flips synchronously and blocks a second caller fired in the same tick
 * (React StrictMode double-invoke, real human double-tap).
 *
 * `run(fn)` returns the fn's resolved value on the first call, and `undefined`
 * on any concurrent reentry. The lock is released after `fn` settles
 * (resolved OR rejected) so the user can legitimately retry on failure.
 */
export function createRetryLock() {
  let locked = false;
  return {
    isLocked: () => locked,
    async run<T>(fn: () => Promise<T>): Promise<T | undefined> {
      if (locked) return undefined;
      locked = true;
      try {
        return await fn();
      } finally {
        locked = false;
      }
    },
  };
}

/**
 * Compute the stage to resume from on retry.
 * Caller-supplied `requestedFromStage` always wins (this is what the
 * "Retry from Stage 2" button passes). Otherwise fall back to the run's
 * recorded error_stage, then 1.
 *
 * Throws if a Stage-2+ retry is requested but no Stage 1 investors are
 * preserved — that would silently re-run Stage 1 and clobber results.
 */
export function computeRetryStartStage(input: {
  requestedFromStage?: number | null;
  errorStage?: number | null;
  investorCount: number;
  /**
   * True when Stage 2 has already produced persisted results (either a
   * `pipeline_state.s2` checkpoint exists or `contact_results` is
   * non-empty). Lets us safely default to resuming at Stage 3 instead of
   * silently rewinding to Stage 1 when `error_stage` is null (e.g. a
   * crash that didn't reach setError). Rewinding drops the s2 checkpoint
   * and triggers a full ~32-batch contact-finder re-fan-out — the exact
   * runaway pattern that produced the $347 Dover Energy run.
   */
  hasStage2Results?: boolean;
}): number {
  const fallback = input.hasStage2Results
    ? 3
    : input.investorCount > 0
      ? 2
      : 1;
  const stage = input.requestedFromStage ?? input.errorStage ?? fallback;
  if (stage >= 2 && input.investorCount === 0) {
    throw new Error(
      "Cannot retry from stage 2+: no Stage 1 investor results to preserve.",
    );
  }
  return stage;
}